// static/table_engine_src/format/format_core.js
import { clampInt } from "../util/number.js";
import { parseNumber } from "./format_numbers.js";

export function isNumericCol(col) {
  if (!col) return false;
  if (col.type === "number") return true;
  if (col.kind === "output" && col.format && typeof col.format === "object") return true;
  return false;
}

// DISPLAY = CANONICAL * scale
export function colScale(col) {
  const f = (col && typeof col === "object" && col.format && typeof col.format === "object")
    ? parseNumber(col.format.scale, NaN)
    : NaN;

  if (Number.isFinite(f) && f !== 0) return f;

  const s = (col && typeof col === "object") ? parseNumber(col.scale, NaN) : NaN;
  if (Number.isFinite(s) && s !== 0) return s;

  return 1;
}

export function normalizeFormat(col) {
  const defaults =
    arguments.length >= 2 && arguments[1] && typeof arguments[1] === "object"
      ? arguments[1]
      : {};

  const colFmt =
    col && typeof col === "object" && col.format && typeof col.format === "object"
      ? col.format
      : {};

  const f = { ...defaults, ...colFmt };
  const dp0 = parseNumber(f.dp, NaN);
  const dp = Number.isFinite(dp0) ? clampInt(dp0, 0, 12) : 4;
  const int = !!f.int;
  const strip = (f.strip === undefined) ? false : !!f.strip;
  const group = !!(f.group === true || f.thousands === true);
  const userDp = (f.userDp === undefined) ? (col && col.kind === "output" ? false : true) : !!f.userDp;
  const roundPayload = !!f.roundPayload;
  const blankValue = (f.blankValue !== undefined) ? f.blankValue : undefined;
  const sci = !!(f.sci === true || f.scientific === true);

  const scale0 = parseNumber(f.scale, NaN);
  const scale = (Number.isFinite(scale0) && scale0 !== 0) ? scale0 : 1;

  const locale = typeof f.locale === "string" && f.locale.trim() !== "" ? f.locale.trim() : "";
  const decimalSep =
    typeof f.decimalSep === "string" && f.decimalSep
      ? f.decimalSep
      : typeof f.decimal === "string" && f.decimal
        ? f.decimal
        : "";
  const groupSep =
    typeof f.groupSep === "string" && f.groupSep
      ? f.groupSep
      : typeof f.thousandSep === "string" && f.thousandSep
        ? f.thousandSep
        : typeof f.thousandsSep === "string" && f.thousandsSep
          ? f.thousandsSep
          : "";

  return {
    dp,
    int,
    strip,
    group,
    userDp,
    roundPayload,
    blankValue,
    sci,
    scale,
    locale,
    decimalSep,
    groupSep,
  };
}

export function displayFromCanonical(x, fmt) {
  const n = parseNumber(x, NaN);
  if (!Number.isFinite(n)) return NaN;
  return n * (fmt?.scale ?? 1);
}

export function canonicalFromDisplay(x, fmt) {
  const n = parseNumber(x, NaN);
  if (!Number.isFinite(n)) return NaN;
  return n / (fmt?.scale ?? 1);
}

/**
 * Factory: create “effective format” helpers that depend on dp overrides + hidden engine rules.
 *
 * ctx requirements:
 *  - ctx.getDpOverride(colKey) -> number|null
 */
export function makeEffectiveFormat(ctx) {
  const specNumFmt =
    ctx?.spec && typeof ctx.spec === "object" && ctx.spec.numberFormat && typeof ctx.spec.numberFormat === "object"
      ? ctx.spec.numberFormat
      : {};

  const normalize =
    typeof ctx?.normalizeFormat === "function"
      ? (col) => ctx.normalizeFormat(col)
      : (col) => normalizeFormat(col, specNumFmt);

  function effectiveFormatForCol(col) {
    const fmt = normalize(col);
    if (!col || !col.key) return fmt;
    if (!isNumericCol(col)) return fmt;

    // dp meaningless for int
    if (fmt.int) return fmt;

    // If userDp=false, ignore overrides entirely
    if (!fmt.userDp) return fmt;

    const colFmt = col && typeof col === "object" ? col.format : null;
    const lockDp = !!(colFmt && typeof colFmt === "object" && colFmt.lockDp === true);
    if (lockDp) return fmt;

    const ov = typeof ctx.getDpOverride === "function" ? ctx.getDpOverride(col.key) : null;
    if (ov !== null && Number.isFinite(ov)) fmt.dp = clampInt(ov, 0, 12);

    return fmt;
  }

  function effectiveFormatForEl(el, col) {
    const fmt = effectiveFormatForCol(col);
    if (!el || !el.dataset) return fmt;

    // Only treat dp/int/strip/group as explicit per-cell when:
    //  - teCellFmt=1 OR teAutoFormat !== "1"
    const explicit = (el.dataset.teCellFmt === "1") || (el.dataset.teAutoFormat !== "1");
    if (!explicit) return fmt;

    if (el.dataset.int != null && el.dataset.int !== "") {
      const s = String(el.dataset.int).toLowerCase();
      fmt.int = (s === "1" || s === "true");
    }
    if (el.dataset.dp != null && el.dataset.dp !== "") {
      const dp = Number(el.dataset.dp);
      if (Number.isFinite(dp)) fmt.dp = clampInt(dp, 0, 12);
    }
    if (el.dataset.strip != null && el.dataset.strip !== "") {
      const s = String(el.dataset.strip).toLowerCase();
      fmt.strip = (s === "1" || s === "true");
    }
    if (el.dataset.group != null && el.dataset.group !== "") {
      const s = String(el.dataset.group).toLowerCase();
      fmt.group = (s === "1" || s === "true");
    } else if (el.dataset.thousands != null && el.dataset.thousands !== "") {
      const s = String(el.dataset.thousands).toLowerCase();
      fmt.group = (s === "1" || s === "true");
    }

    if (el.dataset.locale != null && el.dataset.locale !== "") {
      fmt.locale = String(el.dataset.locale);
    }
    if (el.dataset.decimalSep != null && el.dataset.decimalSep !== "") {
      fmt.decimalSep = String(el.dataset.decimalSep);
    } else if (el.dataset.decimal != null && el.dataset.decimal !== "") {
      fmt.decimalSep = String(el.dataset.decimal);
    }
    if (el.dataset.groupSep != null && el.dataset.groupSep !== "") {
      fmt.groupSep = String(el.dataset.groupSep);
    } else if (el.dataset.thousandSep != null && el.dataset.thousandSep !== "") {
      fmt.groupSep = String(el.dataset.thousandSep);
    }

    return fmt;
  }

  return { effectiveFormatForCol, effectiveFormatForEl };
}
